import {
    Address,
    beginCell,
    Cell,
    Contract,
    contractAddress,
    ContractProvider,
    Sender,
    SendMode,
    Slice,
} from 'ton-core';
import { royaltyCellToConfig, royaltyConfigToCell } from './Royalty';

export type DeployerConfig = {
    adminAddress: Address;
    royalty: Cell;
    price: bigint;
    daoCode: Cell;
    codes: Cell;
};

export function deployerConfigToCell(config: DeployerConfig): Cell {
    return beginCell()
        .storeAddress(config.adminAddress)
        .storeCoins(config.price)
        .storeRef(config.royalty)
        .storeRef(config.daoCode)
        .storeRef(config.codes)
        .endCell();
}

export class Deployer implements Contract {
    constructor(readonly address: Address, readonly init?: { code: Cell; data: Cell }) {}

    static createFromAddress(address: Address) {
        return new Deployer(address);
    }

    static createFromConfig(config: DeployerConfig, code: Cell, workchain = 0) {
        const data = deployerConfigToCell(config);
        const init = { code, data };
        return new Deployer(contractAddress(workchain, init), init);
    }

    async sendDeploy(provider: ContractProvider, via: Sender, value: bigint) {
        await provider.internal(via, {
            value,
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: beginCell().endCell(),
        });
    }

    async sendUpdateAdmin(
        provider: ContractProvider,
        via: Sender,
        value: bigint,
        newAdmin: Address,
        queryId: number = Date.now()
    ) {
        await provider.internal(via, {
            value,
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: beginCell()
                .storeUint(0x75c83df9, 32)
                .storeUint(queryId, 64) // op, queryId
                .storeAddress(newAdmin)
                .endCell(),
        });
    }

    async sendUpdateCodes(
        provider: ContractProvider,
        via: Sender,
        value: bigint,
        daoCode: Cell,
        codes: Cell,
        queryId: number = Date.now()
    ) {
        await provider.internal(via, {
            value,
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: beginCell()
                .storeUint(0x63c584ec, 32)
                .storeUint(queryId, 64) // op, queryId
                .storeRef(daoCode)
                .storeRef(codes)
                .endCell(),
        });
    }

    async sendUpdatePrice(
        provider: ContractProvider,
        via: Sender,
        value: bigint,
        price: bigint,
        royaltyAddress: Address,
        numerator: number = 10,
        denominator: number = 100,
        queryId: number = Date.now()
    ) {
        await provider.internal(via, {
            value,
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: beginCell()
                .storeUint(0x2ecfe864, 32)
                .storeUint(queryId, 64) // op, queryId
                .storeCoins(price)
                .storeRef(royaltyConfigToCell({ royaltyAddress, numerator, denominator }))
                .endCell(),
        });
    }

    async sendWithdrawTons(provider: ContractProvider, via: Sender, value: bigint, queryId: number = Date.now()) {
        await provider.internal(via, {
            value,
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: beginCell().storeUint(0x6d8e5e3c, 32).storeUint(queryId, 64).endCell(),
        });
    }

    async sendWithdrawJettons(
        provider: ContractProvider,
        via: Sender,
        value: bigint,
        childWallet: Address,
        amount: bigint,
        customPayload: Slice | null,
        queryId: number = Date.now()
    ) {
        await provider.internal(via, {
            value,
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body: beginCell()
                .storeUint(0x768a50b2, 32)
                .storeUint(queryId, 64)
                .storeAddress(childWallet)
                .storeCoins(amount)
                .storeMaybeSlice(customPayload)
                .endCell(),
        });
    }

    static mintDaoMessage(options: {
        ownerAddress: Address;
        quorumNominator: number;
        quorumDenominator: number;
        jettonAmount: bigint;
        content: Cell;
        forwardTonAmount: bigint;
        totalTonAmount: bigint;
        queryId?: number;
    }) {
        return beginCell()
            .storeUint(0x66e62d80, 32)
            .storeUint(options.queryId ?? Date.now(), 64)
            .storeAddress(options.ownerAddress)
            .storeUint(options.quorumNominator, 8)
            .storeUint(options.quorumDenominator, 8)
            .storeCoins(options.jettonAmount)
            .storeRef(options.content)
            .storeCoins(options.forwardTonAmount)
            .storeCoins(options.totalTonAmount)
            .endCell();
    }

    async sendMintDao(
        provider: ContractProvider,
        via: Sender,
        value: bigint,
        options: {
            ownerAddress: Address;
            quorumNominator: number;
            quorumDenominator: number;
            jettonAmount: bigint;
            content: Cell;
            forwardTonAmount: bigint;
            totalTonAmount: bigint;
            queryId?: number;
        }
    ) {
        await provider.internal(via, {
            value,
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            bounce: false,
            body: Deployer.mintDaoMessage(options),
        });
    }

    async getDaoAddress(provider: ContractProvider, quorumNominator: number, quorumDominator: number, content: Cell) {
        let res = await provider.get('get_dao_address', [
            { type: 'int', value: BigInt(quorumNominator) },
            { type: 'int', value: BigInt(quorumDominator) },
            { type: 'cell', cell: content },
        ]);
        return res.stack.readAddress();
    }

    async getContractData(provider: ContractProvider) {
        let res = await provider.get('get_contract_data', []);
        let admin = res.stack.readAddress();
        let price = res.stack.readBigNumber();
        let royalty = res.stack.readCell();
        let daoCode = res.stack.readCell();
        let codes = res.stack.readCell();

        const royaltyData = royaltyCellToConfig(royalty);
        return {
            admin,
            price,
            daoCode,
            codes,
            royalty,
            royaltyData,
        };
    }
}
